import { useEffect, useState } from "react";
import { format } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Copy, Trash2, Plus, CheckCircle2, AlertCircle, ClipboardPaste, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";

/**
 * Shared dialog for managing iCal sync: shows export URL for each room and
 * lets the host add import URLs from external channels (Airbnb / Booking.com).
 * Used both from "Disponibilità & Prezzi" (Channel Manager) and from the
 * "Calendario" tab of the Dashboard.
 */
export default function IcalConfigDialog({ open, onOpenChange, rooms, onSaved, onCopy }) {
  const [drafts, setDrafts] = useState({}); // {room_id: [urls]}
  const [savingId, setSavingId] = useState(null);
  const [testStatus, setTestStatus] = useState({}); // {"room_id-idx": {state: 'idle'|'testing'|'ok'|'error', reason?}}

  useEffect(() => {
    if (open) {
      const obj = {};
      (rooms || []).forEach((r) => {
        obj[r.id] = [...(r.ical_urls || []), ""];
      });
      setDrafts(obj);
      setTestStatus({});
    }
  }, [open, rooms]);

  const statusKey = (roomId, idx) => `${roomId}-${idx}`;
  const setRowStatus = (roomId, idx, value) => {
    setTestStatus((prev) => ({ ...prev, [statusKey(roomId, idx)]: value }));
  };
  const getRowStatus = (roomId, idx) => testStatus[statusKey(roomId, idx)] || { state: "idle" };

  const updateUrl = (roomId, idx, value) => {
    setDrafts((prev) => {
      const next = { ...prev };
      const arr = [...(next[roomId] || [])];
      arr[idx] = value;
      next[roomId] = arr;
      return next;
    });
    // Reset test status when URL changes
    setRowStatus(roomId, idx, { state: "idle" });
  };

  const addRow = (roomId) => {
    setDrafts((prev) => ({ ...prev, [roomId]: [...(prev[roomId] || []), ""] }));
  };

  const removeRow = (roomId, idx) => {
    setDrafts((prev) => {
      const arr = [...(prev[roomId] || [])];
      arr.splice(idx, 1);
      return { ...prev, [roomId]: arr.length ? arr : [""] };
    });
  };

  const save = async (roomId) => {
    setSavingId(roomId);
    const urls = (drafts[roomId] || []).filter((u) => u && u.trim());
    try {
      const { data } = await api.put(`/admin/ical-config/${roomId}`, { urls });
      if (data?.warnings?.length) {
        toast.warning(
          `Salvato, ma ${data.warnings.length} link non raggiungibili al momento (errore temporaneo). Riprova il sync più tardi.`
        );
      } else {
        toast.success("URL salvati e verificati");
      }
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message, { duration: 10000 });
        // Mark failing rows visually
        const failedUrls = new Set((detail.failures || []).map((f) => f.url));
        (drafts[roomId] || []).forEach((u, idx) => {
          if (u && failedUrls.has(u.trim())) {
            const fail = (detail.failures || []).find((f) => f.url === u.trim());
            setRowStatus(roomId, idx, { state: "error", reason: fail?.reason || "Link non valido" });
          }
        });
      } else {
        toast.error(detail || "Errore salvataggio");
      }
    } finally {
      setSavingId(null);
    }
  };

  const testUrl = async (roomId, idx) => {
    const url = (drafts[roomId] || [])[idx];
    if (!url || !url.trim()) {
      toast.error("URL vuoto");
      return;
    }
    setRowStatus(roomId, idx, { state: "testing" });
    try {
      const { data } = await api.post("/admin/ical-config/test", { url: url.trim() });
      if (data.ok) {
        setRowStatus(roomId, idx, { state: "ok", reason: data.reason });
        toast.success("Link valido");
      } else {
        setRowStatus(roomId, idx, { state: "error", reason: data.reason });
        toast.error(data.reason || "Link non valido", { duration: 8000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Test fallito";
      setRowStatus(roomId, idx, { state: "error", reason: msg });
      toast.error(msg);
    }
  };

  const handleCopy = async (txt) => {
    if (onCopy) return onCopy(txt);
    const ok = await copyToClipboard(txt);
    if (ok) toast.success("Copiato");
    else toast.error("Copia non riuscita — seleziona e copia manualmente");
  };

  const handlePaste = async (roomId, idx) => {
    try {
      // Modern Clipboard API (works on iOS 13.4+, Chrome, Safari, Firefox)
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        toast.error("Appunti vuoti — copia prima un URL");
        return;
      }
      const trimmed = text.trim();
      // Basic URL sanity check
      if (!/^https?:\/\//i.test(trimmed)) {
        toast.error("Negli appunti non c'è un URL valido");
        return;
      }
      updateUrl(roomId, idx, trimmed);
      toast.success("URL incollato");
    } catch (err) {
      toast.error("Permesso negato — tocca il campo e incolla a mano");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Link2 className="w-5 h-5" /> Sincronizzazione iCal
          </DialogTitle>
          <DialogDescription>
            Esporta il tuo calendario verso Airbnb / Booking.com e importa i loro calendari per
            evitare doppie prenotazioni.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {(rooms || []).map((room) => (
            <section
              key={room.id}
              className="border border-[#E8E2D9] rounded-xl p-4 bg-[#FDFBF7]"
              data-testid={`ical-room-${room.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="font-semibold text-[#1E3A5F]">{room.name?.it}</h4>
                  <p className="text-[11px] text-[#635F59] mt-0.5">
                    {room.external_events
                      ? `${room.external_events} eventi importati`
                      : "Nessun evento esterno"}
                    {room.last_sync && (
                      <> · ultimo sync {format(new Date(room.last_sync), "dd/MM/yyyy HH:mm")}</>
                    )}
                  </p>
                </div>
              </div>

              {/* Export URL */}
              <div className="mb-4">
                <Label className="text-xs text-[#635F59] flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Export — incolla questo URL
                  in Airbnb/Booking
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={room.export_url}
                    readOnly
                    className="h-9 text-xs bg-white font-mono"
                    data-testid={`export-url-${room.slug}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleCopy(room.export_url)}
                    className="bg-[#1E3A5F] hover:bg-[#152A45] text-white shrink-0"
                    data-testid={`copy-export-${room.slug}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Import URLs */}
              <div>
                <Label className="text-xs text-[#635F59] flex items-center gap-1.5">
                  <AlertCircle className="w-3 h-3 text-sky-600" /> Import — incolla qui gli URL iCal
                  di Airbnb/Booking
                </Label>
                <div className="space-y-2 mt-1.5">
                  {(drafts[room.id] || [""]).map((url, idx) => {
                    const rowStatus = getRowStatus(room.id, idx);
                    return (
                    <div key={`${room.id}-row-${idx}`} className="space-y-1">
                      <div className="flex gap-2">
                      <Input
                        type="url"
                        value={url}
                        onChange={(e) => updateUrl(room.id, idx, e.target.value)}
                        placeholder="https://www.airbnb.it/calendar/ical/..."
                        className={`h-9 text-xs bg-white ${
                          rowStatus.state === "error"
                            ? "border-rose-400 focus-visible:ring-rose-300"
                            : rowStatus.state === "ok"
                            ? "border-emerald-400 focus-visible:ring-emerald-300"
                            : ""
                        }`}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        inputMode="url"
                        data-testid={`import-url-${room.slug}-${idx}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handlePaste(room.id, idx)}
                        className="shrink-0 text-[#1E3A5F] border-[#1E3A5F]/30"
                        title="Incolla dagli appunti"
                        data-testid={`paste-url-${room.slug}-${idx}`}
                      >
                        <ClipboardPaste className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => testUrl(room.id, idx)}
                        disabled={!url?.trim() || rowStatus.state === "testing"}
                        className="shrink-0 text-sky-700 border-sky-200"
                        title="Testa il link"
                        data-testid={`test-url-${room.slug}-${idx}`}
                      >
                        {rowStatus.state === "testing" ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <span className="text-[10px] font-semibold px-1">Testa</span>
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => removeRow(room.id, idx)}
                        className="shrink-0 text-rose-600 border-rose-200"
                        title="Rimuovi"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      </div>
                      {rowStatus.state === "ok" && (
                        <p className="text-[11px] text-emerald-700 flex items-center gap-1 pl-1">
                          <CheckCircle2 className="w-3 h-3" /> {rowStatus.reason || "Link valido"}
                        </p>
                      )}
                      {rowStatus.state === "error" && (
                        <p className="text-[11px] text-rose-700 flex items-center gap-1 pl-1" data-testid={`url-error-${room.slug}-${idx}`}>
                          <XCircle className="w-3 h-3" /> {rowStatus.reason}
                        </p>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between gap-2 mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addRow(room.id)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi URL
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => save(room.id)}
                    disabled={savingId === room.id}
                    className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
                    data-testid={`save-ical-${room.slug}`}
                  >
                    {savingId === room.id ? "Salvo..." : "Salva URL"}
                  </Button>
                </div>
              </div>
            </section>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
